"use client"
import { LoadScript, GoogleMap, InfoWindow, Marker } from '@react-google-maps/api';
import { MarkerClusterer, SuperClusterAlgorithm, defaultOnClusterClickHandler } from '@googlemaps/markerclusterer';
import { useState, useEffect, useRef, useMemo } from "react";
import { Professional } from "@/hooks/useSearch";
import LocationIcon from "@/components/IconsTsx/LocationIcon";
import GrayPhoneIcon from "@/components/IconsTsx/GrayPhoneIcon";
import ArrowLeft from "@/components/IconsTsx/ArrowLeft";
import { ArrowRight } from "@/components/IconsTsx/ArrowRight";
import styles from "./infoWindow.module.css";
import "./google-maps-global.css";
import { getMapLocationKey } from "@/lib/locationGrouping";
import {
  focusMapOnResults,
  filterMapOutliers,
  type ProfessionalWithCoords,
} from "@/lib/mapViewHelpers";
import { trackMapPinClicked, trackNumberClicked, trackMapsClicked } from "@/lib/mixpanelClient";

type MapLocationGroup = {
  key: string;
  professionals: ProfessionalWithCoords[];
};

const useWindowSize = () => {
  const [windowSize, setWindowSize] = useState({ width: 0, height: 0 });
  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    setIsClient(true);
    setWindowSize({ width: window.innerWidth, height: window.innerHeight });

    const handleResize = () => {
      setWindowSize({ width: window.innerWidth, height: window.innerHeight });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return { windowSize, isClient };
};

const getContainerStyle = (windowSize: { width: number; height: number }) => {
  const baseStyle = { width: "100%", borderRadius: "32px" };

  if (windowSize.width < 768) return { ...baseStyle, height: "350px" };
  if (windowSize.width < 1024) return { ...baseStyle, height: "400px" };
  return { ...baseStyle, height: "500px" };
};

const center = { lat: -34.6037, lng: -58.3816 };
const DEFAULT_ZOOM = 12;

const ensureMarkerVisible = (map: google.maps.Map, coords: { lat: number; lng: number }) => {
  const latLng = new google.maps.LatLng(coords.lat, coords.lng);
  const bounds = map.getBounds();

  if (bounds?.contains(latLng)) {
    return;
  }

  map.panTo(coords);
};

interface GoogleMapViewProps {
  professionals: Professional[];
  selectedProfessional: Professional | null;
  onProfessionalSelect: (professional: Professional | null) => void;
  userCenter?: { lat: number; lng: number } | null;
}

// Icono "Tu ubicación": punto navy con halo, distinto de los marcadores numerados.
const createUserIcon = () => {
  if (typeof google === 'undefined' || !google.maps) {
    return undefined;
  }
  const svg = `
    <svg width="40" height="40" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg">
      <circle cx="20" cy="20" r="11" fill="#002654" opacity="0.16"/>
      <circle cx="20" cy="20" r="6" fill="#002654" stroke="#ffffff" stroke-width="2.5"/>
    </svg>
  `;
  return {
    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
    scaledSize: new google.maps.Size(40, 40),
    anchor: new google.maps.Point(20, 20),
  };
};

// Función para validar y convertir coordenadas
const validateCoordinates = (
  lat: string | number | null | undefined,
  lng: string | number | null | undefined
): { lat: number; lng: number } | null => {
  const latNum = typeof lat === 'string' ? parseFloat(lat) : lat;
  const lngNum = typeof lng === 'string' ? parseFloat(lng) : lng;

  if (
    typeof latNum === 'number' &&
    typeof lngNum === 'number' &&
    !isNaN(latNum) &&
    !isNaN(lngNum) &&
    latNum !== 0 &&
    lngNum !== 0 &&
    latNum >= -90 &&
    latNum <= 90 &&
    lngNum >= -180 &&
    lngNum <= 180
  ) {
    return { lat: latNum, lng: lngNum };
  }

  return null;
};

const isSameProfessional = (a: Professional, b: Professional) =>
  a.CODIGO === b.CODIGO && a.NOMBRE === b.NOMBRE && a.APELLIDO === b.APELLIDO;

const createDotIcon = (isSelected: boolean): google.maps.Icon | undefined => {
  if (typeof google === 'undefined' || !google.maps) return undefined;

  const size = isSelected ? 22 : 18;
  const svg = `
    <svg width="${size}" height="${size}" viewBox="0 0 ${size} ${size}" xmlns="http://www.w3.org/2000/svg">
      <circle cx="${size / 2}" cy="${size / 2}" r="${size / 2 - 1}" fill="#6399F8" stroke="#ffffff" stroke-width="2"/>
      ${isSelected ? `<circle cx="${size / 2}" cy="${size / 2}" r="${size / 2 - 0.5}" fill="none" stroke="#002564" stroke-width="1.5" opacity="0.35"/>` : ''}
    </svg>
  `;

  return {
    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
    scaledSize: new google.maps.Size(size, size),
    anchor: new google.maps.Point(size / 2, size / 2),
  };
};

const createClusterIcon = (): google.maps.Icon | undefined => {
  if (typeof google === 'undefined' || !google.maps) return undefined;

  const size = 28;
  const svg = `
    <svg width="${size}" height="${size}" viewBox="0 0 ${size} ${size}" xmlns="http://www.w3.org/2000/svg">
      <circle cx="${size / 2}" cy="${size / 2}" r="${size / 2 - 2}" fill="#6399F8" stroke="#ffffff" stroke-width="3"/>
      <circle cx="${size / 2}" cy="${size / 2}" r="${size / 2 - 6}" fill="none" stroke="#002564" stroke-width="1.5" opacity="0.25"/>
    </svg>
  `;

  return {
    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
    scaledSize: new google.maps.Size(size, size),
    anchor: new google.maps.Point(size / 2, size / 2),
  };
};

export function GoogleMapView({ professionals, selectedProfessional, onProfessionalSelect, userCenter }: GoogleMapViewProps) {
  const { windowSize, isClient } = useWindowSize();
  const containerStyle = isClient ? getContainerStyle(windowSize) : {
    width: "100%",
    height: "500px",
    borderRadius: "32px",
  };
  const [mapReady, setMapReady] = useState(false);
  const mapRef = useRef<google.maps.Map | null>(null);
  const clustererRef = useRef<MarkerClusterer | null>(null);
  const markersRef = useRef<google.maps.Marker[]>([]);
  const markerListenersRef = useRef<google.maps.MapsEventListener[]>([]);
  const lastSelectedRef = useRef<string | null>(null);
  const lastProfessionalsKeyRef = useRef<string>('');

  const handleOpenMaps = (e: React.MouseEvent, professional: Professional) => {
    e.stopPropagation();
    if (professional.LOCATION_LAT && professional.LOCATION_LONG) {
      trackMapsClicked({ prestadorId: professional.CODIGO }); // #18
      const url = `https://www.google.com/maps?q=${professional.LOCATION_LAT},${professional.LOCATION_LONG}`;
      window.open(url, '_blank');
    }
  };

  const handleCall = (e: React.MouseEvent, professional: Professional) => {
    e.stopPropagation();
    if (professional.TELEFONO) {
      trackNumberClicked({ prestadorId: professional.CODIGO, numero: professional.TELEFONO }); // #17
      window.location.href = `tel:${professional.TELEFONO}`;
    }
  };

  const professionalsWithCoords = useMemo(
    () =>
      professionals
        .map((professional) => {
          const coords = validateCoordinates(professional.LOCATION_LAT, professional.LOCATION_LONG);
          return coords ? { ...professional, validCoords: coords } : null;
        })
        .filter(Boolean) as ProfessionalWithCoords[],
    [professionals]
  );

  const mapProfessionals = useMemo(
    () => filterMapOutliers(professionalsWithCoords),
    [professionalsWithCoords]
  );

  const mapLocationGroups = useMemo(() => {
    const groups = mapProfessionals.reduce((acc, professional) => {
      const key = getMapLocationKey(professional, professional.validCoords);
      if (!key) return acc;

      if (!acc[key]) acc[key] = [];
      acc[key].push(professional);
      return acc;
    }, {} as Record<string, ProfessionalWithCoords[]>);

    return Object.entries(groups).map(([key, groupProfessionals]) => ({
      key,
      professionals: groupProfessionals,
    }));
  }, [mapProfessionals]);

  const groupedProfessionals = useMemo(
    () =>
      mapLocationGroups.reduce((acc, group) => {
        acc[group.key] = group.professionals;
        return acc;
      }, {} as Record<string, ProfessionalWithCoords[]>),
    [mapLocationGroups]
  );

  const getProfessionalsAtLocation = (professional: Professional) => {
    const coords = validateCoordinates(professional.LOCATION_LAT, professional.LOCATION_LONG);
    const key = getMapLocationKey(professional, coords);
    if (!key) return [] as ProfessionalWithCoords[];

    return groupedProfessionals[key] ?? [];
  };

  const getMarkerTitle = (professionalsAtLocation: ProfessionalWithCoords[]) => {
    if (professionalsAtLocation.length > 1) {
      return `${professionalsAtLocation.length} profesionales en esta dirección`;
    }

    const prof = professionalsAtLocation[0];
    return `${prof.APELLIDO}, ${prof.NOMBRE}`;
  };

  const handleLocationClick = (professionalsAtLocation: ProfessionalWithCoords[]) => {
    const firstProfessional = professionalsAtLocation[0];

    trackMapPinClicked({ // #12
      prestadorId: firstProfessional?.CODIGO ?? '',
      zoomLevel: mapRef.current?.getZoom(),
      cantidadEnPin: professionalsAtLocation.length,
    });

    if (professionalsAtLocation.length > 1 && selectedProfessional) {
      const currentIndex = professionalsAtLocation.findIndex((prof) =>
        isSameProfessional(prof, selectedProfessional)
      );

      if (currentIndex !== -1) {
        const nextIndex = (currentIndex + 1) % professionalsAtLocation.length;
        onProfessionalSelect(professionalsAtLocation[nextIndex]);
        return;
      }
    }

    onProfessionalSelect(firstProfessional);
  };

  const navigateColocatedProfessional = (
    professionalsAtLocation: ProfessionalWithCoords[],
    direction: 1 | -1
  ) => {
    if (!selectedProfessional || professionalsAtLocation.length <= 1) return;

    const currentIndex = professionalsAtLocation.findIndex((prof) =>
      isSameProfessional(prof, selectedProfessional)
    );

    const baseIndex = currentIndex === -1 ? 0 : currentIndex;
    const nextIndex =
      (baseIndex + direction + professionalsAtLocation.length) % professionalsAtLocation.length;

    onProfessionalSelect(professionalsAtLocation[nextIndex]);
  };

  // Si todavía no hay prestadores con coordenadas pero el usuario eligió una
  // ubicación, centrar el mapa en esa ubicación para dar contexto.
  useEffect(() => {
    const map = mapRef.current;
    if (map && userCenter && professionalsWithCoords.length === 0) {
      map.panTo(userCenter);
      map.setZoom(12);
    }
  }, [userCenter, professionalsWithCoords.length]);

  // Efecto para hacer fly-to cuando cambia el profesional seleccionado
  useEffect(() => {
    const map = mapRef.current;
    if (!mapReady || !map || typeof google === 'undefined') return;

    const professionalsKey = professionals
      .map((professional) => `${professional.CODIGO}-${professional.MATRICULA}-${professional.NOMBRE}-${professional.APELLIDO}`)
      .join('|');
    // Incluimos el centro de cercanía en la clave para reencuadrar cuando el
    // usuario cambia de ubicación aunque la lista de prestadores sea la misma.
    const focusKey = `${professionalsKey}|${userCenter ? `${userCenter.lat.toFixed(3)},${userCenter.lng.toFixed(3)}` : ''}`;

    if (focusKey === lastProfessionalsKeyRef.current) return;

    lastProfessionalsKeyRef.current = focusKey;
    lastSelectedRef.current = null;
    focusMapOnResults(map, professionalsWithCoords, userCenter);
  }, [professionals, professionalsWithCoords, mapReady, userCenter]);

  useEffect(() => {
    const map = mapRef.current;
    if (!mapReady || !map || !selectedProfessional) return;

    const currentKey = `${selectedProfessional.CODIGO}-${selectedProfessional.NOMBRE}-${selectedProfessional.APELLIDO}`;
    if (currentKey === lastSelectedRef.current) return;

    lastSelectedRef.current = currentKey;

    const coords = validateCoordinates(selectedProfessional.LOCATION_LAT, selectedProfessional.LOCATION_LONG);
    if (!coords) return;

    ensureMarkerVisible(map, coords);
  }, [selectedProfessional, mapReady]);

  useEffect(() => {
    if (!selectedProfessional) {
      lastSelectedRef.current = null;
    }
  }, [selectedProfessional]);

  useEffect(() => {
    const map = mapRef.current;
    if (!mapReady || !map || typeof google === 'undefined') return;

    markerListenersRef.current.forEach((listener) => listener.remove());
    markerListenersRef.current = [];
    markersRef.current.forEach((marker) => marker.setMap(null));
    markersRef.current = [];

    if (clustererRef.current) {
      clustererRef.current.clearMarkers();
      clustererRef.current.setMap(null);
      clustererRef.current = null;
    }

    const newMarkers = mapLocationGroups.map(({ key, professionals: professionalsAtLocation }) => {
      const firstProfessional = professionalsAtLocation[0];
      const isSelected = Boolean(
        selectedProfessional &&
          professionalsAtLocation.some((prof) => isSameProfessional(prof, selectedProfessional))
      );

      const marker = new google.maps.Marker({
        position: firstProfessional.validCoords,
        icon: createDotIcon(isSelected),
        title: getMarkerTitle(professionalsAtLocation),
      });

      const listener = marker.addListener('click', () => {
        handleLocationClick(professionalsAtLocation);
      });

      markerListenersRef.current.push(listener);
      marker.set('locationKey', key);
      return marker;
    });

    markersRef.current = newMarkers;

    clustererRef.current = new MarkerClusterer({
      map,
      markers: newMarkers,
      algorithm: new SuperClusterAlgorithm({
        maxZoom: 14,
        radius: 70,
        minPoints: 3,
      }),
      onClusterClick: (event, cluster, map) => {
        defaultOnClusterClickHandler(event, cluster, map);
      },
      renderer: {
        render: ({ count, position }) =>
          new google.maps.Marker({
            position,
            icon: createClusterIcon(),
            title: `${count} ubicaciones cercanas. Acercá el mapa para verlas por separado.`,
            zIndex: Number(google.maps.Marker.MAX_ZINDEX) + count,
          }),
      },
    });

    return () => {
      markerListenersRef.current.forEach((listener) => listener.remove());
      markerListenersRef.current = [];
      clustererRef.current?.clearMarkers();
      clustererRef.current?.setMap(null);
      clustererRef.current = null;
      markersRef.current.forEach((marker) => marker.setMap(null));
      markersRef.current = [];
    };
  }, [mapReady, mapLocationGroups, selectedProfessional]);

  const onMapLoad = (map: google.maps.Map) => {
    mapRef.current = map;
    setMapReady(true);

    if (professionalsWithCoords.length > 0 || userCenter) {
      focusMapOnResults(map, professionalsWithCoords, userCenter);
    }
  };

  const onMapUnmount = () => {
    setMapReady(false);
    mapRef.current = null;
  };

  const handleMapClick = () => {
    onProfessionalSelect(null);
  };

  return (
    <LoadScript googleMapsApiKey={process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_ID || ""}>
      <GoogleMap
        mapContainerStyle={containerStyle}
        center={center}
        zoom={DEFAULT_ZOOM}
        onLoad={onMapLoad}
        onUnmount={onMapUnmount}
        onClick={handleMapClick}
        options={{
          zoomControl: true,
          mapTypeControl: false,
          scaleControl: false,
          streetViewControl: false,
          rotateControl: false,
          fullscreenControl: false,
          gestureHandling: 'greedy',
        }}
      >
        {/* Marcador de la ubicación del usuario (centro de cercanía).
            Los marcadores de prestadores los gestiona main de forma imperativa
            (MarkerClusterer), por eso acá solo va el de "Tu ubicación". */}
        {userCenter && (
          <Marker
            position={userCenter}
            icon={createUserIcon()}
            title="Tu ubicación"
            zIndex={9999}
          />
        )}

        {/* InfoWindow para el profesional seleccionado */}
        {selectedProfessional && (() => {
          const coords = validateCoordinates(selectedProfessional.LOCATION_LAT, selectedProfessional.LOCATION_LONG);
          if (!coords) return null;

          const professionalsAtLocation = getProfessionalsAtLocation(selectedProfessional);
          const currentIndex = professionalsAtLocation.findIndex((prof) =>
            isSameProfessional(prof, selectedProfessional)
          );
          const hasMultipleAtLocation = professionalsAtLocation.length > 1;
          const infoWindowKey = `${coords.lat.toFixed(5)}-${coords.lng.toFixed(5)}`;

          return (
            <InfoWindow
              key={infoWindowKey}
              position={coords}
              options={{
                disableAutoPan: true,
                maxWidth: 340,
                pixelOffset: typeof google !== 'undefined' ? new google.maps.Size(0, -4) : undefined,
              }}
            >
              <div className={styles.content} onClick={(e) => e.stopPropagation()}>
                {hasMultipleAtLocation && (
                  <div className={styles.locationNav}>
                    <button
                      type="button"
                      className={styles.navButton}
                      aria-label="Profesional anterior en esta dirección"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigateColocatedProfessional(professionalsAtLocation, -1);
                      }}
                    >
                      <ArrowLeft width={16} height={16} />
                    </button>
                    <span className={styles.locationNavText}>
                      {currentIndex + 1} de {professionalsAtLocation.length} en esta dirección
                    </span>
                    <button
                      type="button"
                      className={styles.navButton}
                      aria-label="Profesional siguiente en esta dirección"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigateColocatedProfessional(professionalsAtLocation, 1);
                      }}
                    >
                      <ArrowRight width={16} height={16} />
                    </button>
                  </div>
                )}

                <div className={styles.header}>
                  <div className={styles.name}>
                    <h4>{selectedProfessional.APELLIDO},</h4>
                    <h5>{selectedProfessional.NOMBRE}</h5>
                  </div>
                </div>

                <div className={styles.details}>
                  <div className={styles.detailItem}>
                    <LocationIcon />
                    <span>{selectedProfessional.DIRECCION}, {selectedProfessional.LOCALIDAD}</span>
                  </div>
                  {selectedProfessional.TELEFONO && (
                    <div className={styles.detailItem}>
                      <GrayPhoneIcon />
                      <span>{selectedProfessional.TELEFONO}</span>
                    </div>
                  )}
                </div>

                {(selectedProfessional.LOCATION_LAT !== 0 && selectedProfessional.LOCATION_LONG !== 0) || selectedProfessional.TELEFONO ? (
                  <div className={styles.actions}>
                    {selectedProfessional.LOCATION_LAT !== 0 && selectedProfessional.LOCATION_LONG !== 0 && (
                      <button
                        className={styles.btnMaps}
                        onClick={(e) => handleOpenMaps(e, selectedProfessional)}
                      >
                        Abrir en Maps
                      </button>
                    )}
                    {selectedProfessional.TELEFONO && (
                      <button
                        className={styles.btnCall}
                        onClick={(e) => handleCall(e, selectedProfessional)}
                      >
                        Llamar
                      </button>
                    )}
                  </div>
                ) : null}
              </div>
            </InfoWindow>
          );
        })()}
      </GoogleMap>
    </LoadScript>
  );
}
